import { forwardRef, Ref, useEffect, useState } from 'react';
import { InfoTextContainer } from '../input/input.styles';
import { MarginProps } from '../base-margin/base-margin.component';
import {
  addDateSeparatosAutomatically,
  transformToDecimal,
  TValidationMethod,
} from '../../utils/forms/forms.utils';
import FormField from '../input/form-field.component';
import { useFormField } from '../../hooks/use-form-field.hook';
import { InputMessageType } from '../input-message/input-message.component';

export const MAX_CHAR_LENGTH = 255;

interface Props {
  isOptional?: boolean;
  name: string;
  placeholder?: string;
  label: string;
  isLabelHidden?: boolean;
  labelEndElement?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  isManualDateInput?: boolean;
  hint?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  maxLength?: number;
  disabled?: boolean;
  validationMethod?: TValidationMethod;
  infoText?: string; // can control whether to display a text (e.g. $ sign) in the right side of an input
  $borderNone?: boolean;
  showValidationError?: boolean;
  prefix?: {
    element: React.ReactNode;
    isClickable?: boolean;
  };
  suffix?: {
    element: React.ReactNode;
    isClickable?: boolean;
  };
  disableBlur?: boolean;
  isHidden?: boolean;
  showSkeleton?: boolean;
  skeletonClassName?: string;
  readOnly?: boolean;
  showInfoMessage?: boolean;
  infoMessageType?: InputMessageType;
  infoMessage?: string;
  triggerValidation?: boolean;
  errorColor?: string;
  tabIndex?: number;
  incrementControls?: boolean;
  minValue?: number;
  maxValue?: number;
  useFlagEmojiFont?: boolean;
  showMaxLengthCounter?: boolean;
}

const FormFieldText = (
  {
    name,
    placeholder,
    label,
    type = 'text',
    $borderNone,
    infoText,
    inputMode = 'text',
    isHidden,
    isLabelHidden,
    hint,
    maxLength = MAX_CHAR_LENGTH,
    disabled = false,
    validationMethod = 'default',
    onChange,
    onFocus,
    onBlur,
    showValidationError = true,
    isManualDateInput,
    isOptional,
    labelEndElement,
    prefix,
    suffix,
    disableBlur,
    showSkeleton,
    skeletonClassName,
    readOnly,
    showInfoMessage,
    infoMessageType,
    infoMessage,
    triggerValidation,
    errorColor,
    tabIndex,
    incrementControls = false,
    minValue = 0,
    maxValue,
    useFlagEmojiFont,
    showMaxLengthCounter,
  }: Props & MarginProps,
  ref: Ref<HTMLInputElement>
) => {
  const [currentValidationMethod, setCurrentValidationMethod] =
    useState(validationMethod);
  const [isFocused, setIsFocused] = useState(false);

  const {
    field,
    formikContext,
    setIsTyped,
    t,
    shouldShowCounter,
    errorMessage,
  } = useFormField({
    name,
    validationMethod: currentValidationMethod,
    isFocused,
    maxLength,
    showMaxLengthCounter,
    showValidationError,
  });

  useEffect(() => {
    if (field.value) {
      formikContext.validateField(name);
      setCurrentValidationMethod('always');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentValidationMethod(validationMethod);
  }, [validationMethod]);

  useEffect(() => {
    if (!triggerValidation) return;

    formikContext.validateField(name);
    setCurrentValidationMethod('always');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidation]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    if (inputMode === 'numeric') {
      newValue = newValue.replace(/\D/g, '');
      event.target.value = newValue;
    }
    if (inputMode === 'decimal') {
      newValue = transformToDecimal(event.target.value);
      event.target.value = newValue;
    }
    if (isManualDateInput && placeholder && newValue && field.value) {
      event.target.value = addDateSeparatosAutomatically(
        newValue,
        field.value,
        placeholder
      );
    }
    setCurrentValidationMethod(validationMethod);
    setIsTyped(true);
    field.onChange(event);
    onChange && onChange(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // need setTimeout to access event.target.value, because react instantly clears its value...
    // (event.persist() didn't help).
    // setTimeout(() => {
    // }, 0);
    setIsFocused(false);
    let targetValue = event.target.value.trim();
    if (inputMode === 'decimal' && targetValue) {
      // To remove dot from the not completed decimal like 1. -> 1
      targetValue = String(Number(targetValue));
    }
    formikContext.setFieldValue(name, targetValue);
    if (disableBlur) return;
    field.onBlur(event);
    onBlur && onBlur(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus && onFocus(event);
  };

  return (
    <FormField
      {...field}
      ref={ref}
      type={type}
      placeholder={placeholder ? t(placeholder) : undefined}
      inputMode={inputMode}
      value={field.value ?? ''}
      disabled={disabled}
      readOnly={readOnly}
      name={name}
      $borderNone={$borderNone}
      isOptional={isOptional}
      optionalText={t('optional')}
      hint={hint}
      label={t(label)}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      errorMessage={errorMessage}
      showInfoMessage={showInfoMessage}
      infoMessageType={infoMessageType}
      infoMessage={infoMessage}
      isHidden={isHidden}
      isLabelHidden={isLabelHidden}
      skeletonClassName={skeletonClassName}
      maxLength={maxLength}
      labelEndElement={labelEndElement}
      prefix={prefix}
      errorColor={errorColor}
      incrementControls={incrementControls}
      minValue={minValue}
      maxValue={maxValue}
      suffix={{
        element: (
          <>
            {suffix?.element}
            {infoText && <InfoTextContainer>{t(infoText)}</InfoTextContainer>}
          </>
        ),
        isClickable: suffix?.isClickable,
      }}
      showSkeleton={showSkeleton}
      tabIndex={tabIndex}
      useFlagEmojiFont={useFlagEmojiFont}
      showMaxLengthCounter={shouldShowCounter}
    />
  );
};

export default forwardRef(FormFieldText);
